import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { GraphqlApiService } from '@libs/graphql/api';
import { AuthSdk, type LoginResult, type RegisterSdkInput } from '@libs/graphql/sdk/auth/auth.sdk';
import { PersonaService, personaFromRoles, type Persona } from '@shared/service';

interface StoredSession {
  accessToken: string;
  userId: number;
  persona: Persona;
  roles: string[];
  vendorId: number | null;
  customerId: number | null;
}

/**
 * Real authentication for Trexa. Backs onto the API's custom auth mutations
 * (UserMutations.Custom.cs): login / requestPasswordReset / register. Persona is
 * derived from roles (no tenant layer); the single access token is attached to the
 * generated SDK via GraphqlApiService.setToken. There is no refresh token / Session
 * table — logout just clears the client.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly persona = inject(PersonaService);
  private readonly router  = inject(Router);
  private readonly graphql = inject(GraphqlApiService);
  private readonly authSdk = inject(AuthSdk);
  private readonly SESSION_KEY = 'trexa.auth.session';

  /**
   * Sign in with email + password only. The persona is resolved server-side from the
   * user's roles (no Vendor/Customer tab). Returns the persona; throws with a
   * user-facing message on failure.
   */
  async login(email: string, password: string): Promise<Persona> {
    const res = await this.authSdk.login(email.trim(), password);
    if (!res.success || !res.data?.accessToken) {
      throw new Error(res.error || res.message || 'Invalid email or password.');
    }
    return this.applySession(res.data);
  }

  /**
   * Create an account (vendor or customer). Returns the full result but does NOT
   * activate a session — the caller decides: a new vendor is logged in and sent to
   * onboarding (call applySession), a new customer is asked to log in manually.
   */
  async register(input: RegisterSdkInput): Promise<LoginResult> {
    const res = await this.authSdk.register(input);
    if (!res.success || !res.data?.accessToken) {
      throw new Error(res.error || res.message || 'Could not create your account.');
    }
    return res.data;
  }

  /**
   * Change the signed-in user's password. Identity comes from the JWT server-side,
   * so no id is sent — only the current + new password. Throws with a user-facing
   * message on failure (wrong current password, too short, etc.).
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const res = await this.authSdk.changePassword(currentPassword, newPassword);
    if (!res.success) {
      throw new Error(res.error || res.message || 'Could not update your password.');
    }
  }

  /**
   * Forgot password (no email service): the API resets the account to the fixed
   * default and returns a message. Never throws / never reveals whether the account
   * exists — always returns the message to show on the "sent" screen.
   */
  async requestPasswordReset(email: string): Promise<string> {
    try {
      const res = await this.authSdk.requestPasswordReset(email.trim());
      return res.message || 'If the account exists, its password was reset.';
    } catch {
      return 'If the account exists, its password was reset.';
    }
  }

  async logout(): Promise<void> {
    this.clearSession();
    await this.router.navigateByUrl('/auth/login');
  }

  homeFor(persona: Persona): string {
    return persona === 'admin'    ? '/admin/overview'
      : persona === 'customer'    ? '/customer/discover'
      : '/vendor/dashboard';
  }

  /**
   * Single source of truth for "logged in": true only when a real, unexpired auth
   * session token is present. Used by the auth guard and the customer login-gated
   * actions — do NOT infer login from a bootstrapped/skipAuth persona (a guest browsing
   * the customer module has no token). An expired token is cleared and counts as a guest,
   * so a stale session never lets a "should-be-logged-out" user slip past the gates.
   */
  isAuthenticated(): boolean {
    const token = this.readSession()?.accessToken;
    if (!token) return false;
    if (this.isExpired(token)) {
      this.clearSession();
      return false;
    }
    return true;
  }

  /** APP_INITIALIZER hook: re-hydrate the token + persona from storage. */
  restoreSession(): void {
    // No server-side refresh (no Session table) — just re-attach the stored token.
    this.graphql.setTokenRefresh(async () => this.readSession()?.accessToken ?? null);

    const s = this.readSession();
    if (!s) return;
    // Drop an expired session on boot rather than rehydrating a dead token.
    if (this.isExpired(s.accessToken)) { this.clearSession(); return; }
    this.graphql.setToken(s.accessToken);
    this.persona.setPersona(s.persona, s.userId, {
      vendorId: s.vendorId,
      customerId: s.customerId,
      roles: s.roles,
    });
  }

  // ---- internals -------------------------------------------------------------

  /** Clear the local session (token + storage + persona) without navigating. */
  private clearSession(): void {
    this.graphql.setToken(null);
    localStorage.removeItem(this.SESSION_KEY);
    this.persona.clear();
  }

  /**
   * True when the JWT carries an `exp` claim that has already passed (with a small
   * clock-skew grace). A token without a parseable `exp` is treated as non-expiring
   * (we only reject when we can prove it's stale).
   */
  private isExpired(token: string): boolean {
    const exp = this.tokenExp(token);
    if (exp == null) return false;
    return Date.now() > (exp + 30) * 1000; // 30s grace for clock skew
  }

  /** Decode a JWT's `exp` (seconds since epoch). Returns null if absent/unparseable. */
  private tokenExp(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(json) as { exp?: number };
      return typeof claims.exp === 'number' ? claims.exp : null;
    } catch {
      return null;
    }
  }

  /** Persist the login result, attach the token, and set the derived persona. */
  applySession(data: LoginResult): Persona {
    const persona = (personaFromRoles(data.roles) ?? (data.persona as Persona)) as Persona;
    const session: StoredSession = {
      accessToken: data.accessToken,
      userId: data.user?.id ?? 0,
      persona,
      roles: data.roles ?? [],
      vendorId: data.vendorId ?? null,
      customerId: data.customerId ?? null,
    };
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    this.graphql.setToken(data.accessToken);
    this.persona.setPersona(persona, session.userId, {
      vendorId: session.vendorId,
      customerId: session.customerId,
      roles: session.roles,
    });
    return persona;
  }

  private readSession(): StoredSession | null {
    try {
      const raw = localStorage.getItem(this.SESSION_KEY);
      return raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      return null;
    }
  }
}
